import React, { useMemo } from 'react';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { ListItem } from '@swyftx/aviary/atoms/List';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { SelectProps } from '@swyftx/aviary/atoms/Select';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { ArrowChevronRight, LightMode } from '@swyftx/aviary/icons/outlined';
import { MenuItem } from '@swyftx/aviary/molecules/Menu';

import { DarkModeEnum } from '@shared/enums';
import { cn } from '@shared/utils/lib/ui';

import { capitalize } from '@utils/formatting';

import { observer } from 'mobx-react-lite';
import { useToggleTheme } from 'src/lib/utils/hooks';

type Props = {
  renderOnlyElement?: boolean;
  spotlightElementId?: string;
  size?: SelectProps['size'];
};

const themes: DarkModeEnum[] = [DarkModeEnum.LIGHT, DarkModeEnum.DARK, DarkModeEnum.MIDNIGHT];

const SwyftxThemeSelector: React.FC<Props> = observer(({ size = 'medium', renderOnlyElement, spotlightElementId }) => {
  const { darkMode, setDarkMode } = useToggleTheme();

  const element = useMemo(
    () => (
      <FlexLayout
        alignItems='center'
        spacing={4}
        className='cursor-pointer rounded-8 p-8 hover:bg-color-background-surface-hover'
      >
        <Body size={size} color='primary'>
          {capitalize(darkMode)}
        </Body>
        <ArrowChevronRight className='h-24 w-24 text-color-text-primary' />
      </FlexLayout>
    ),
    [darkMode, size],
  );

  return (
    <Modal
      title='Select theme'
      position='dynamic'
      blockOverlayClose={false}
      triggerElement={
        renderOnlyElement ? (
          element
        ) : (
          <div>
            <MenuItem
              icon={<LightMode width={24} height={24} className='text-color-text-primary' />}
              text='Theme'
              data-spotlightelementid={spotlightElementId}
              endAdornment={<div className='w-auto sm:w-[120px]'>{element}</div>}
            />
          </div>
        )
      }
    >
      <FlexLayout direction='column' spacing={8} className='p-8'>
        {themes.map((theme) => {
          const selected = theme === darkMode;
          return (
            <ListItem
              key={theme}
              onClick={() => setDarkMode(theme)}
              className={cn(selected ? 'bg-color-background-surface-selected text-color-text-selected' : '')}
            >
              <FlexLayout alignItems='center' spacing={8} justifyContent='space-between' className='w-full'>
                <FlexLayout alignItems='center' spacing={8}>
                  <FlexLayout direction='column' alignItems='start'>
                    <Body className='break-all'>{capitalize(theme)}</Body>
                  </FlexLayout>
                </FlexLayout>
              </FlexLayout>
            </ListItem>
          );
        })}
      </FlexLayout>
    </Modal>
  );
});

export { SwyftxThemeSelector };
